/**
 * 인라인 서식 엔진 (P2-①) — execCommand 미사용.
 * 선택 영역을 **문자 단위 모델**로 변환해 마크를 적용/토글한 뒤 다시 렌더한다.
 * 직렬화(inline.js)와 완벽 정합하며 링크/중첩 마크를 보존한다.
 *
 *  - applyInlineMark(content, type, value?): 현재 선택에 마크 적용/토글. 변경 시 true.
 *      · boolean 마크(bold/italic/underline/strike/code): value 생략 → 토글.
 *      · 값 마크(textColor/backgroundColor/fontSize): value 지정 → 설정, null/"default" → 해제.
 *  - readActiveMarks(content): 현재 선택의 활성 마크(툴바 표시용).
 *  - setSelection(content, start, end): 문자 오프셋으로 선택 복원(테스트/내부용).
 *
 * 오프셋은 DOM Range 기준 UTF-16 단위(= range.toString().length).
 */

import { inlineContentToHtml, htmlToInlineContent } from "./serialize/inline.js";

const BOOLEAN_MARKS = ["bold", "italic", "underline", "strike", "code"];
const VALUE_MARKS = ["textColor", "backgroundColor", "fontSize"];

function sameStyles(a, b) {
  const ak = Object.keys(a);
  const bk = Object.keys(b);
  if (ak.length !== bk.length) return false;
  return ak.every((k) => a[k] === b[k]);
}

/**
 * 인라인 배열 → 문자 배열 [{ch, styles, href?}] (UTF-16 단위).
 * text/link가 아닌 인라인 원자(예: checkbox)는 1유닛 { atom } 으로 보존한다 —
 * 서식 적용 시 char 배열을 다시 인라인으로 되돌릴 때 유실되지 않도록(회귀 방지).
 */
function inlineToChars(arr) {
  const out = [];
  for (const node of arr || []) {
    if (node && node.type === "link") {
      const href = node.href || "";
      for (const t of node.content || []) {
        const text = t.text || "";
        const st = t.styles || {};
        for (let i = 0; i < text.length; i++) out.push({ ch: text[i], styles: { ...st }, href });
      }
    } else if (node && node.type === "text") {
      const text = node.text || "";
      const st = node.styles || {};
      for (let i = 0; i < text.length; i++) out.push({ ch: text[i], styles: { ...st } });
    } else if (node && node.type) {
      out.push({ atom: { ...node } }); // 인라인 원자(checkbox 등) — 1유닛 보존, 서식 미적용
    }
  }
  return out;
}

function groupTextRuns(chars) {
  const runs = [];
  for (const c of chars) {
    const prev = runs[runs.length - 1];
    if (prev && sameStyles(prev.styles, c.styles)) prev.text += c.ch;
    else runs.push({ type: "text", text: c.ch, styles: { ...c.styles } });
  }
  return runs;
}

/** 문자 배열 → 인라인 배열 (동일 href는 link로 묶고, 그 안/밖을 스타일 run으로 그룹, 원자는 그대로 방출) */
function charsToInline(chars) {
  const out = [];
  let i = 0;
  while (i < chars.length) {
    const c = chars[i];
    if (c.atom) { out.push(c.atom); i++; continue; } // 인라인 원자(checkbox 등) 보존
    const href = c.href;
    if (href !== undefined) {
      const lc = [];
      while (i < chars.length && !chars[i].atom && chars[i].href === href) {
        lc.push(chars[i]);
        i++;
      }
      out.push({ type: "link", href, content: groupTextRuns(lc) });
    } else {
      const tc = [];
      while (i < chars.length && !chars[i].atom && chars[i].href === undefined) {
        tc.push(chars[i]);
        i++;
      }
      for (const r of groupTextRuns(tc)) out.push(r);
    }
  }
  return out;
}

function applyToChars(chars, start, end, type, value, forceBool) {
  if (start > end) {
    const t = start;
    start = end;
    end = t;
  }
  const slice = chars.slice(start, end).filter((c) => !c.atom); // 원자(checkbox)는 서식 대상 아님
  if (!slice.length) return false;
  if (BOOLEAN_MARKS.includes(type) && value === undefined) {
    // forceBool 지정 시(크로스블록 일괄 토글) 지역 계산 대신 강제: true=적용, false=해제.
    const allHave = forceBool === undefined ? slice.every((c) => c.styles[type]) : !forceBool;
    for (const c of slice) {
      if (allHave) delete c.styles[type];
      else c.styles[type] = true;
    }
  } else {
    for (const c of slice) {
      if (value == null || value === "default") delete c.styles[type];
      else c.styles[type] = value;
    }
  }
  return true;
}

// --- DOM 오프셋 ---
// 유닛 = (ZWSP 제외 텍스트 문자) + (인라인 원자 1) + (하드브레이크 <br> 1, filler 제외).
// inlineToChars/htmlToInlineContent 열거 규칙과 정합(체크박스=1노드, <br>→"\n"=1문자, ZWSP 제거, filler <br> 무시).
const ZWSP = "​";
// 인라인 원자(표 셀 체크박스/순서·불렛 마커) — inlineToChars가 1유닛으로 보존하므로 오프셋도 1유닛으로 세야 정합.
function isAtomEl(n) {
  return (
    n && n.nodeType === 1 && n.hasAttribute &&
    (n.hasAttribute("data-lumir-checkbox") || n.hasAttribute("data-lumir-ol") || n.hasAttribute("data-lumir-ul"))
  );
}
function isHardBr(n) {
  return n && n.nodeType === 1 && n.tagName === "BR" && !(n.hasAttribute && n.hasAttribute("data-lumir-br-filler"));
}
function isUnitEl(n) {
  return isAtomEl(n) || isHardBr(n); // 1유닛으로 세는 요소(원자 + 하드브레이크)
}
function indexInParent(n) {
  return Array.prototype.indexOf.call(n.parentNode.childNodes, n);
}

/** DOM 캐럿(container, offset) → 유닛 오프셋. */
export function caretToOffset(content, container, offset) {
  let count = 0;
  let done = false;
  function walk(node) {
    if (done) return;
    if (node === container && node.nodeType !== 3) {
      const lim = Math.min(offset, node.childNodes.length); // 요소 컨테이너: 앞 offset개 자식만 소비
      for (let i = 0; i < lim; i++) { walk(node.childNodes[i]); if (done) return; }
      done = true;
      return;
    }
    if (node.nodeType === 3) {
      const s = node.nodeValue;
      const lim = node === container ? offset : s.length;
      for (let i = 0; i < lim; i++) if (s[i] !== ZWSP) count++;
      if (node === container) done = true;
      return;
    }
    if (isUnitEl(node)) { count += 1; return; }
    for (const kid of node.childNodes) { walk(kid); if (done) return; }
  }
  walk(content);
  return count;
}

/** 유닛 오프셋 → DOM 캐럿(node, offset). */
export function offsetToCaret(content, target) {
  let total = 0;
  let result = null;
  function walk(node) {
    if (result) return;
    if (node.nodeType === 3) {
      const s = node.nodeValue;
      for (let i = 0; i < s.length; i++) {
        if (s[i] === ZWSP) continue;
        if (total === target) { result = { node, offset: i }; return; }
        total++;
      }
      return;
    }
    if (isUnitEl(node)) {
      if (total === target) { result = { node: node.parentNode, offset: indexInParent(node) }; return; } // 유닛 앞
      total++;
      if (total === target) { result = { node: node.parentNode, offset: indexInParent(node) + 1 }; return; } // 유닛 뒤
      return;
    }
    for (const kid of node.childNodes) { walk(kid); if (result) return; }
  }
  walk(content);
  if (result) return result;
  return { node: content, offset: content.childNodes.length };
}

export function setSelection(content, start, end) {
  const a = offsetToCaret(content, start);
  const b = offsetToCaret(content, end);
  const r = document.createRange();
  try {
    r.setStart(a.node, a.offset);
    r.setEnd(b.node, b.offset);
  } catch (_) {
    r.selectNodeContents(content);
  }
  const s = window.getSelection();
  s.removeAllRanges();
  s.addRange(r);
}

function currentRangeIn(content) {
  const s = window.getSelection();
  if (!s || !s.rangeCount) return null;
  const r = s.getRangeAt(0);
  if (!content.contains(r.startContainer) || !content.contains(r.endContainer)) return null;
  return r;
}

/** 현재 선택 영역에 인라인 마크 적용/토글. 변경되면 true. */
export function applyInlineMark(content, type, value) {
  if (!content) return false;
  const r = currentRangeIn(content);
  if (!r || r.collapsed) return false; // collapsed=보류(추후 pending marks)
  const start = caretToOffset(content, r.startContainer, r.startOffset);
  const end = caretToOffset(content, r.endContainer, r.endOffset);
  const chars = inlineToChars(htmlToInlineContent(content));
  if (!applyToChars(chars, start, end, type, value)) return false;
  content.innerHTML = inlineContentToHtml(charsToInline(chars));
  setSelection(content, start, end);
  return true;
}

/** 현재 선택 영역을 링크로 감싸기/해제. href 비면 해제. 변경되면 true. */
export function applyLink(content, href) {
  if (!content) return false;
  const r = currentRangeIn(content);
  if (!r || r.collapsed) return false;
  const start = caretToOffset(content, r.startContainer, r.startOffset);
  const end = caretToOffset(content, r.endContainer, r.endOffset);
  const a = Math.min(start, end), b = Math.max(start, end);
  const chars = inlineToChars(htmlToInlineContent(content));
  const slice = chars.slice(a, b);
  if (!slice.some((c) => !c.atom)) return false; // 텍스트가 없으면(원자만) 변경 없음
  for (const c of slice) {
    if (c.atom) continue; // 원자(checkbox)는 링크 대상 아님
    if (!href) delete c.href; // 해제 → 일반 텍스트
    else c.href = href;
  }
  content.innerHTML = inlineContentToHtml(charsToInline(chars));
  setSelection(content, a, b);
  return true;
}

/**
 * 현재 커서(또는 선택)에 인라인 콘텐츠(text/link 노드 배열)를 삽입.
 * 선택이 있으면 대체, collapsed면 삽입. 삽입 후 캐럿은 삽입분 끝. 변경되면 true.
 * (URL 붙여넣기 시 커서 인라인 링크·다단어 텍스트 자동 링크화에 사용 — inline.js 직렬화와 정합)
 */
export function insertInlineAtCursor(content, nodes) {
  if (!content || !nodes || !nodes.length) return false;
  const r = currentRangeIn(content);
  if (!r) return false;
  const start = caretToOffset(content, r.startContainer, r.startOffset);
  const end = caretToOffset(content, r.endContainer, r.endOffset);
  const a = Math.min(start, end), b = Math.max(start, end);
  const chars = inlineToChars(htmlToInlineContent(content));
  const ins = inlineToChars(nodes);
  if (!ins.length) return false;
  const next = chars.slice(0, a).concat(ins, chars.slice(b));
  content.innerHTML = inlineContentToHtml(charsToInline(next));
  setSelection(content, a + ins.length, a + ins.length);
  return true;
}

/** 현재 선택의 활성 마크 상태(툴바 표시용). */
export function readActiveMarks(content) {
  const empty = { bold: false, italic: false, underline: false, strike: false, code: false, textColor: null, backgroundColor: null, fontSize: null };
  if (!content) return empty;
  const r = currentRangeIn(content);
  if (!r) return empty;
  const start = caretToOffset(content, r.startContainer, r.startOffset);
  const end = caretToOffset(content, r.endContainer, r.endOffset);
  const chars = inlineToChars(htmlToInlineContent(content));
  const slice = chars.slice(Math.min(start, end), Math.max(start, end)).filter((c) => !c.atom); // 원자 제외
  if (!slice.length) return empty;
  const out = { ...empty };
  for (const m of BOOLEAN_MARKS) out[m] = slice.every((c) => !!c.styles[m]);
  for (const m of VALUE_MARKS) {
    const first = slice[0].styles[m] != null ? slice[0].styles[m] : null;
    out[m] = slice.every((c) => (c.styles[m] != null ? c.styles[m] : null) === first) ? first : null;
  }
  return out;
}

// ─────────────────────────────────────────────────────────────────────────────
// 크로스블록(여러 .content 섬에 걸친) 일괄 서식 — cross-block-selection이 산출한
// covered = [{island, start, end, full}] 를 받아 각 섬을 독립적으로 재작성한다.
// window.getSelection()을 읽지 않으므로(네이티브 선택이 비어 있어도 동작) 크로스 선택에 사용.
// ─────────────────────────────────────────────────────────────────────────────

/**
 * 크로스블록 일괄 인라인 마크 적용/토글. 변경되면 true.
 *  - boolean 마크(value 미지정): 전 범위 균일 규칙 — 커버된 모든 섬의 모든 비-atom 문자가
 *    이미 마크를 가지면 해제, 아니면 적용(BlockNote/Word 정합).
 *  - 값 마크(textColor/backgroundColor/fontSize): value 설정, null/"default"면 해제.
 * 선택은 건드리지 않는다(호출자가 하이라이트를 재페인팅). 오프셋(유닛 수)은 서식 후에도 불변.
 */
export function applyInlineMarkAcross(covered, type, value) {
  if (!covered || !covered.length) return false;
  const isBoolToggle = BOOLEAN_MARKS.includes(type) && value === undefined;
  let forceBool;
  if (isBoolToggle) {
    let any = false, allHave = true;
    for (const c of covered) {
      const chars = inlineToChars(htmlToInlineContent(c.island));
      for (const ch of chars.slice(c.start, c.end)) {
        if (ch.atom) continue;
        any = true;
        if (!ch.styles[type]) allHave = false;
      }
    }
    if (!any) return false;
    forceBool = !allHave; // 전부 보유 → 해제(false), 아니면 → 적용(true)
  }
  let changed = false;
  for (const c of covered) {
    const chars = inlineToChars(htmlToInlineContent(c.island));
    const did = isBoolToggle
      ? applyToChars(chars, c.start, c.end, type, undefined, forceBool)
      : applyToChars(chars, c.start, c.end, type, value);
    if (did) {
      c.island.innerHTML = inlineContentToHtml(charsToInline(chars));
      changed = true;
    }
  }
  return changed;
}

// ── Phase 2(크로스블록 편집)용 char 모델 헬퍼 — cross-block-range가 재사용 ──
/** content(.content 섬)의 인라인을 char 배열([{ch,styles,href?}|{atom}])로. */
export function contentToChars(content) {
  return inlineToChars(htmlToInlineContent(content));
}
/** char 배열을 다시 HTML 문자열로(직렬화 정합). */
export function charsToHtml(chars) {
  return inlineContentToHtml(charsToInline(chars));
}
/** content의 [start,end) 구간을 인라인 노드 배열(저장 폼)로 잘라 반환(부분 복사용). */
export function sliceContentInline(content, start, end) {
  return charsToInline(inlineToChars(htmlToInlineContent(content)).slice(start, end));
}

/** 크로스블록 선택의 활성 마크 상태(툴바 표시용) — covered의 모든 비-atom 문자를 종합. */
export function readActiveMarksAcross(covered) {
  const empty = { bold: false, italic: false, underline: false, strike: false, code: false, textColor: null, backgroundColor: null, fontSize: null };
  if (!covered || !covered.length) return empty;
  const all = [];
  for (const c of covered) {
    const chars = inlineToChars(htmlToInlineContent(c.island));
    for (const ch of chars.slice(c.start, c.end)) if (!ch.atom) all.push(ch);
  }
  if (!all.length) return empty;
  const out = { ...empty };
  for (const m of BOOLEAN_MARKS) out[m] = all.every((c) => !!c.styles[m]);
  for (const m of VALUE_MARKS) {
    const first = all[0].styles[m] != null ? all[0].styles[m] : null;
    out[m] = all.every((c) => (c.styles[m] != null ? c.styles[m] : null) === first) ? first : null;
  }
  return out;
}
